import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useApiFetch } from '../lib/api'
import { toAgentSlug } from '../lib/agentSlug'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { AGENT_CATEGORIES } from '../lib/types'
import type { AgentCategory, CreateAgentRequest, AgentCreated } from '../lib/types'

interface NewAgentModalProps {
  onClose: () => void
  onError: (message: string) => void
}

export function NewAgentModal({ onClose, onError }: NewAgentModalProps) {
  const navigate = useNavigate()
  const apiFetch = useApiFetch()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const [displayName, setDisplayName] = useState('')
  const [category, setCategory] = useState<AgentCategory>('Insights & Analytics')
  const modalRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<Element | null>(null)

  useFocusTrap(modalRef)

  useEffect(() => {
    triggerRef.current = document.activeElement
    return () => {
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus()
      }
    }
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const mutation = useMutation({
    mutationFn: (req: CreateAgentRequest) =>
      apiFetch<AgentCreated>('/api/v1/agents', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: (agent) => {
      void queryClient.invalidateQueries({ queryKey: ['agents'] })
      navigate(`/agents/${toAgentSlug(agent.displayName)}/agent-dna`)
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : t('common.somethingWentWrong')
      onError(message)
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (mutation.isPending) return
    mutation.mutate({ displayName: displayName.trim(), category })
  }

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-agent-modal-title"
        className="modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id="new-agent-modal-title">{t('newAgentModal.title')}</h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p>{t('newAgentModal.subtitle')}</p>
            <div className="form-group">
              <label
                htmlFor="new-agent-display-name"
                className="form-label"
              >
                {t('newAgentModal.nameLabel')}
              </label>
              <input
                id="new-agent-display-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t('newAgentModal.namePlaceholder')}
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label
                htmlFor="new-agent-category"
                className="form-label"
              >
                {t('newAgentModal.categoryLabel')}
              </label>
              <select
                id="new-agent-category"
                value={category}
                onChange={(e) => setCategory(e.target.value as AgentCategory)}
                className="form-select"
              >
                {AGENT_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost"
            >
              {t('newAgentModal.cancel')}
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="btn btn-primary"
              style={{ opacity: mutation.isPending ? 0.7 : 1 }}
            >
              {mutation.isPending ? t('newAgentModal.creating') : t('newAgentModal.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
